import React, { useEffect, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { supabase, CLINIC_ID } from "./supabase.js";
import { friendlyError } from "./lib/format.js";
import { loadBhpRequests, updateBhpRequestStatus, loadBhpItemsWithStock, initBhpStockForItem, updateBhpStockLimits, loadProcurementData, getActiveDivisions, loadClinicModules } from "./data.js";
import { BHPRequestModal } from "./BHPRequestModal.jsx";
import { ProcurementPage } from "./ProcurementPage.jsx";

const emptyForm = {
  item_code: "",
  name: "",
  category: "",
  unit: "",
  minimum_stock: "",
  target_stock: "",
  divisions: [],
  status: "ACTIVE",
};

const STATUS_OPTIONS = [
  { value: "ACTIVE", label: "Aktif" },
  { value: "INACTIVE", label: "Nonaktif" },
];

// Fisioterapi belum aktif di XV, jadi tidak boleh ikut konfigurasi BHP.
const BHP_DIVISION_VALUES = new Set(["WELLNESS", "ESTETIK", "DENTAL", "DOKTER_UMUM", "APOTIK"]);

// DIV_TABS dibangun dinamis dari modul aktif

function stockStatus(row) {
  const cur = Number(row.current_stock ?? 0);
  const min = Number(row.minimum_stock ?? 0);
  if (cur <= 0) return "habis";
  if (cur <= min) return "limit";
  return "ok";
}

export function MasterBHPPage({ identity, onChanged, refreshKey = 0 }) {
  const [activeModules, setActiveModules] = useState(identity?.activeModules || { wellness: true, estetik: true, dental: true });
  const activeBhpDivisions = getActiveDivisions(activeModules).filter((division) => BHP_DIVISION_VALUES.has(division.value));
  const DIV_TABS = [
    { key: "GUDANG", label: "Stok Gudang" },
    ...activeBhpDivisions.map(d => ({ key: d.value, label: `Stok ${d.label}` })),
  ];
  const DIVISION_OPTIONS = [
    { value: "ALL", label: "Semua divisi" },
    ...activeBhpDivisions,
  ];
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");
  const [form, setForm]         = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [search, setSearch]     = useState("");
  const [divTab, setDivTab]     = useState("GUDANG");
  const [showAlert, setShowAlert] = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [mainTab, setMainTab]   = useState("master");   // "master" | "requests" | "procurement"
  const [procData, setProcData] = useState(null);
  const [procLoading, setProcLoading] = useState(false);
  const [requestItem, setRequestItem] = useState(null); // item yang akan di-request
  const [requestItems, setRequestItems] = useState([]);
  const [purchaseItems, setPurchaseItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [requests, setRequests] = useState([]);
  const [reqLoading, setReqLoading] = useState(false);
  const [reqError, setReqError] = useState("");
  const [receivingId, setReceivingId] = useState(null);
  const [receiveQty, setReceiveQty]   = useState("");

  function locationLabel(location) {
    return DIV_TABS.find((tab) => tab.key === location)?.label || location;
  }

  // divTab → location di bhp_stock
  // Gudang membaca stok pusat, selain itu membaca lokasi divisi.
  function tabToLocation(tab) {
    return tab;
  }

  async function fetchItems(tab = divTab) {
    setLoading(true);
    setError("");
    try {
      const data = await loadBhpItemsWithStock(tabToLocation(tab));
      setRows(data);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchItems(divTab); }, [divTab, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchRequests() {
    setReqLoading(true);
    setReqError("");
    try {
      setRequests(await loadBhpRequests());
    } catch (err) {
      setReqError(friendlyError(err));
    } finally {
      setReqLoading(false);
    }
  }

  useEffect(() => {
    if (mainTab === "requests") fetchRequests();
  }, [mainTab]);

  useEffect(() => {
    if (!identity?.activeModules) {
      loadClinicModules().then(setActiveModules).catch(() => {});
    }
  }, []); // eslint-disable-line  

  async function confirmReceive(req) {
    try {
      await updateBhpRequestStatus(req.id, "DITERIMA", {
        bhp_item_id:  req.bhp_item_id,
        received_qty: Number(receiveQty),
      });
      setReceivingId(null);
      setReceiveQty("");
      await fetchRequests();
      await fetchItems();
    } catch (err) { alert(friendlyError(err)); }
  }

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function resetForm() {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
  }

  function openCreateForm() {
    setForm({ ...emptyForm, divisions: DIVISION_OPTIONS.filter((o) => o.value !== "ALL").map((o) => o.value) });
    setEditingId(null);
    setError("");
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function startEdit(row) {
    setEditingId(row.id);
    setForm({
      item_code:     row.item_code     || "",
      name:          row.name          || "",
      category:      row.category      || "",
      unit:          row.unit          || "",
      minimum_stock: row.minimum_stock ?? "",
      target_stock:  row.target_stock  ?? "",
      divisions:     row.divisions === null ? DIVISION_OPTIONS.filter((o) => o.value !== "ALL").map((o) => o.value) : (row.divisions || (row.division && row.division !== "ALL" ? [row.division] : [])),
      status:        row.status        || "ACTIVE",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  // ── CSV Import ────────────────────────────────────────────────────────────
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [csvRows, setCsvRows]           = useState([]);   // parsed preview rows
  const [csvError, setCsvError]         = useState('');
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvResult, setCsvResult]       = useState(null); // { ok, fail }

  function downloadTemplate() {
    const headers = [
      'kode_item', 'nama_obat', 'kategori', 'satuan',
      'stok_minimum', 'stok_target', 'divisi', 'status',
    ];
    const example = [
      'OBT-001', 'Amoxicillin 500mg', 'Antibiotik', 'kapsul',
      '50', '200', 'APOTIK', 'ACTIVE',
    ];
    const notes = [
      '# PETUNJUK PENGISIAN TEMPLATE OBAT FARMASI',
      '# Hapus baris yang diawali tanda # sebelum mengimpor',
      '# kode_item   : Kode unik obat dari pemasok (boleh kosong)',
      '# nama_obat   : Nama obat lengkap termasuk kekuatan dosis - WAJIB ISI',
      '# kategori    : Contoh: Antibiotik, Antihipertensi, Vitamin, Antasida, dll',
      '# satuan      : Contoh: tablet, kapsul, botol, strip, ampul, sachet',
      '# stok_minimum: Angka minimum stok sebelum perlu reorder (default 0)',
      '# stok_target : Angka target stok normal (default 0)',
      '# divisi      : Isi APOTIK untuk obat farmasi (atau ALL untuk semua)',
      '# status      : ACTIVE atau INACTIVE',
    ];
    const lines = [
      ...notes,
      headers.join(','),
      example.join(','),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = 'template_obat_farmasi.csv'; a.click();
    URL.revokeObjectURL(url);
  }

  function parseCsvFile(file) {
    setCsvError(''); setCsvRows([]); setCsvResult(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text  = e.target.result;
        const lines = text.split(/\r?\n/).filter((l) => l.trim() && !l.startsWith('#'));
        if (lines.length < 2) { setCsvError('File CSV kosong atau hanya berisi header.'); return; }
        const header = lines[0].split(',').map((h) => h.trim().toLowerCase().replace(/\s+/g,'_'));
        const COL = {
          kode:   ['kode_item','kode','item_code','code'],
          nama:   ['nama_obat','nama','name','item_name'],
          kat:    ['kategori','category'],
          satuan: ['satuan','unit'],
          min:    ['stok_minimum','minimum_stock','min_stock','min'],
          target: ['stok_target','target_stock','target'],
          divisi: ['divisi','division'],
          status: ['status'],
        };
        function colIdx(aliases) {
          for (const a of aliases) { const i = header.indexOf(a); if (i >= 0) return i; }
          return -1;
        }
        const idx = {
          kode:   colIdx(COL.kode),
          nama:   colIdx(COL.nama),
          kat:    colIdx(COL.kat),
          satuan: colIdx(COL.satuan),
          min:    colIdx(COL.min),
          target: colIdx(COL.target),
          divisi: colIdx(COL.divisi),
          status: colIdx(COL.status),
        };
        if (idx.nama < 0) { setCsvError('Kolom "nama_obat" tidak ditemukan di CSV.'); return; }
        const parsed = [];
        for (let i = 1; i < lines.length; i++) {
          const cols = lines[i].split(',').map((c) => c.trim().replace(/^"|"$/g,''));
          const nama = idx.nama >= 0 ? cols[idx.nama] : '';
          if (!nama) continue;
          const divisi = (idx.divisi >= 0 ? cols[idx.divisi] : 'APOTIK').toUpperCase() || 'APOTIK';
          parsed.push({
            _row:         i + 1,
            item_code:    idx.kode   >= 0 ? cols[idx.kode]   : '',
            name:         nama,
            category:     idx.kat    >= 0 ? cols[idx.kat]    : '',
            unit:         idx.satuan >= 0 ? cols[idx.satuan] : '',
            minimum_stock:idx.min    >= 0 ? Number(cols[idx.min])    || 0 : 0,
            target_stock: idx.target >= 0 ? Number(cols[idx.target]) || 0 : 0,
            division:     ['WELLNESS','ESTETIK','DENTAL','DOKTER_UMUM','APOTIK','ALL'].includes(divisi) ? divisi : 'APOTIK',
            status:       (idx.status >= 0 ? cols[idx.status] : 'ACTIVE').toUpperCase() === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE',
          });
        }
        if (parsed.length === 0) { setCsvError('Tidak ada baris data valid di CSV.'); return; }
        setCsvRows(parsed);
      } catch (err) {
        setCsvError('Gagal membaca file: ' + err.message);
      }
    };
    reader.readAsText(file, 'UTF-8');
  }

  async function handleCsvImport() {
    if (!csvRows.length) return;
    setCsvImporting(true); setCsvResult(null);
    let ok = 0, fail = 0;
    for (const row of csvRows) {
      try {
        const payload = {
          clinic_id:  CLINIC_ID,
          item_code:  row.item_code || null,
          name:       row.name,
          category:   row.category || null,
          unit:       row.unit || null,
          division:   row.division,
          status:     row.status,
        };
        const { data: inserted, error: insertError } = await supabase
          .from('bhp_items').insert(payload).select('id').single();
        if (insertError) throw insertError;
        if (inserted?.id) {
          await initBhpStockForItem(inserted.id);
          await updateBhpStockLimits(inserted.id, 'GUDANG', {
            minimum_stock: row.minimum_stock,
            target_stock:  row.target_stock,
          });
        }
        ok++;
      } catch {
        fail++;
      }
    }
    setCsvResult({ ok, fail });
    setCsvImporting(false);
    if (ok > 0) await fetchItems();
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) { setError("Nama item BHP wajib diisi."); return; }
    if (!form.divisions.length) { setError("Pilih minimal satu poli pemakai BHP."); return; }
    setSaving(true);
    setError("");
    try {
      const payload = {
        clinic_id:     CLINIC_ID,
        item_code:     form.item_code.trim() || null,
        name:          form.name.trim(),
        category:      form.category.trim() || null,
        unit:          form.unit.trim() || null,
        division:      form.divisions.length === DIVISION_OPTIONS.length - 1 ? "ALL" : form.divisions[0],
        status:        form.status   || "ACTIVE",
      };
      const stockLimits = {
        minimum_stock: Number(form.minimum_stock) || 0,
        target_stock:  Number(form.target_stock)  || 0,
      };
      const location = tabToLocation(divTab);
      if (editingId) {
        const { error: updateError } = await supabase.from("bhp_items").update(payload)
          .eq("clinic_id", CLINIC_ID)
          .eq("id", editingId);
        if (updateError) throw updateError;
        const { error: usageDeleteError } = await supabase.from("bhp_item_divisions").delete().eq("clinic_id", CLINIC_ID).eq("bhp_item_id", editingId);
        if (usageDeleteError) throw usageDeleteError;
        const { error: usageInsertError } = await supabase.from("bhp_item_divisions").insert(form.divisions.map((division) => ({ clinic_id: CLINIC_ID, bhp_item_id: editingId, division })));
        if (usageInsertError) throw usageInsertError;
        await updateBhpStockLimits(editingId, location, stockLimits);
      } else {
        const { data: inserted, error: insertError } = await supabase
          .from("bhp_items").insert(payload).select("id").single();
        if (insertError) throw insertError;
        // Inisialisasi bhp_stock untuk semua lokasi
        if (inserted?.id) await initBhpStockForItem(inserted.id);
        if (inserted?.id) {
          const { error: usageInsertError } = await supabase.from("bhp_item_divisions").insert(form.divisions.map((division) => ({ clinic_id: CLINIC_ID, bhp_item_id: inserted.id, division })));
          if (usageInsertError) throw usageInsertError;
        }
        if (inserted?.id) await updateBhpStockLimits(inserted.id, location, stockLimits);
      }
      resetForm();
      await fetchItems();
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(row) {
    if (!window.confirm(`Hapus item "${row.name}"? Stok saat ini: ${row.current_stock} ${row.unit || ""}.`)) return;
    setError("");
    try {
      const { error: deleteError } = await supabase.from("bhp_items").delete()
        .eq("clinic_id", CLINIC_ID)
        .eq("id", row.id);
      if (deleteError) throw deleteError;
      if (editingId === row.id) resetForm();
      await fetchItems();
    } catch (err) {
      setError(friendlyError(err));
    }
  }

  // Semua item aktif yang habis atau di bawah limit
  const alertItems = useMemo(() =>
    rows.filter((r) => r.status === "ACTIVE" && stockStatus(r) !== "ok"),
    [rows]
  );

  // Tabel: filter divisi + search
  const filtered = useMemo(() => {
    let list = rows;
    if (divTab !== "GUDANG") {
      list = list.filter((r) => {
        if (divTab !== "GUDANG") return usedBy(r, divTab);
        return true;
      });
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((r) =>
        r.name.toLowerCase().includes(q) ||
        (r.item_code || "").toLowerCase().includes(q) ||
        (r.category  || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [rows, divTab, search]);

  const selectedRows = useMemo(
    () => filtered.filter((row) => selectedIds.has(row.id)),
    [filtered, selectedIds]
  );

  function toggleSelected(id) {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAllVisible() {
    setSelectedIds((current) => {
      const selectable = filtered.map((row) => row.id);
      const allSelected = selectable.length > 0 && selectable.every((id) => current.has(id));
      const next = new Set(current);
      if (allSelected) selectable.forEach((id) => next.delete(id));
      else selectable.forEach((id) => next.add(id));
      return next;
    });
  }

  function openBatchRequest(rowsToRequest = selectedRows) {
    if (!rowsToRequest.length) return;
    openStockAction(rowsToRequest);
  }

  async function openStockAction(rowsToRequest) {
    const itemsToProcess = Array.isArray(rowsToRequest) ? rowsToRequest.filter(Boolean) : [rowsToRequest].filter(Boolean);
    if (!itemsToProcess.length) return;
    setError("");
    if (divTab === "GUDANG") {
      if (!canProcurement) {
        setError("Stok Gudang diproses melalui Purchase Order. Anda belum memiliki akses Procurement.");
        return;
      }
      setRequestItem(null);
      setRequestItems([]);
      setPurchaseItems(itemsToProcess);
      await switchTab("procurement");
      return;
    }
    if (itemsToProcess.length === 1) setRequestItem(itemsToProcess[0]);
    else setRequestItems(itemsToProcess);
  }

  function divLabel(d) {
    const option = DIVISION_OPTIONS.find((row) => row.value === d);
    if (option) return option.label;
    return "Semua";
  }

  function selectedDivisions(row) {
    if (Array.isArray(row.divisions)) return row.divisions;
    if (row.divisions === null || row.division === "ALL") {
      return DIVISION_OPTIONS.filter((option) => option.value !== "ALL").map((option) => option.value);
    }
    return row.division ? [row.division] : [];
  }

  function usageLabel(row) {
    const selected = selectedDivisions(row);
    const all = DIVISION_OPTIONS.filter((option) => option.value !== "ALL").map((option) => option.value);
    if (selected.length === all.length && all.every((division) => selected.includes(division))) return "Semua poli";
    return selected.map((d) => divLabel(d)).join(", ") || "Belum diatur";
  }

  function usedBy(row, division) {
    return selectedDivisions(row).includes(division);
  }

  function divTone(d) {
    if (d === "ESTETIK") return "info";
    if (d === "DENTAL") return "warning";
    if (d === "WELLNESS") return "positive";
    return "neutral";
  }

  const PIPELINE = [
    { status: "REQUESTED",  label: "Diminta",    next: "DIPROSES",   nextLabel: "Proses",   color: "#2563eb" },
    { status: "DIPROSES",   label: "Diproses",   next: "DIKIRIM",    nextLabel: "Kirim",    color: "#d97706" },
    { status: "DIKIRIM",    label: "Dikirim",    next: "DITERIMA",   nextLabel: "Terima",   color: "#7c3aed" },
    { status: "DITERIMA",   label: "Diterima",   next: null,         nextLabel: null,       color: "#16a34a" },
    { status: "DIBATALKAN", label: "Dibatalkan", next: null,         nextLabel: null,       color: "#dc2626" },
  ];
  function pipelineInfo(status) {
    return PIPELINE.find((p) => p.status === status) || { label: status, color: "#6b7280" };
  }

  const pendingCount = requests.filter((r) => !["DITERIMA","DIBATALKAN"].includes(r.status)).length;

  async function switchTab(key) {
    setMainTab(key);
    if (key === "procurement" && !procData && !procLoading) {
      setProcLoading(true);
      try { setProcData(await loadProcurementData()); }
      catch { /* ignore */ }
      finally { setProcLoading(false); }
    }
  }

  const canProcurement = identity?.permissions?.has("inventory.write") ||
    ["Admin","Inventory"].includes(identity?.role?.role_name || "");

  return (
    <div className="page-stack">
      {/* ── Main tabs ── */}
      <div style={{ display: "flex", gap: 8, padding: "0 4px" }}>
        {[
          { key: "master",      label: "Master BHP" },
          { key: "requests",    label: `Permintaan Internal${pendingCount ? ` (${pendingCount})` : ""}` },
          ...(canProcurement ? [{ key: "procurement", label: "Procurement" }] : []),
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => switchTab(t.key)}
            style={{
              padding: "7px 20px", borderRadius: 8, border: "none", fontSize: 14, fontWeight: 600,
              cursor: "pointer",
              background: mainTab === t.key ? "#2f6f4f" : "#f3f4f6",
              color:      mainTab === t.key ? "#fff"    : "#374151",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {mainTab === "requests" && (
        <article className="panel">
          <div className="panel-head" style={{ marginBottom: 16 }}>
            <div>
              <span className="eyebrow">Internal Gudang</span>
              <h3>Permintaan Internal BHP</h3>
            </div>
            <button className="button button-quiet" onClick={fetchRequests} disabled={reqLoading}>
              {reqLoading ? "Memuat…" : "Refresh"}
            </button>
          </div>
          {reqError && <div className="alert alert-danger">{reqError}</div>}

          {/* Pipeline ringkasan */}
          <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
            {PIPELINE.slice(0, 4).map((p) => {
              const count = requests.filter((r) => r.status === p.status).length;
              return (
                <div key={p.status} style={{ background: p.color + "12", border: `1px solid ${p.color}30`, borderRadius: 8, padding: "8px 16px", minWidth: 100 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: p.color, textTransform: "uppercase" }}>{p.label}</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: "#111" }}>{count}</div>
                </div>
              );
            })}
          </div>

          {/* Daftar request */}
          {reqLoading ? (
            <div className="content-loading"><div className="loader" /><span>Memuat permintaan…</span></div>
          ) : !requests.length ? (
            <p className="muted">Belum ada permintaan stok.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {requests.map((req) => {
                const info = pipelineInfo(req.status);
                const isDone = req.status === "DITERIMA" || req.status === "DIBATALKAN";
                const isReceiving = receivingId === req.id;
                const tgl = req.requested_at ? new Date(req.requested_at).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }) : "—";
                return (
                  <div key={req.id} style={{
                    border: `1px solid ${info.color}30`,
                    borderLeft: `4px solid ${info.color}`,
                    borderRadius: 10,
                    padding: "12px 16px",
                    background: isDone ? "#f9fafb" : "#fff",
                    display: "flex", alignItems: "flex-start", gap: 14, flexWrap: "wrap",
                  }}>
                    <div style={{ flex: 1, minWidth: 180 }}>
                      <div style={{ fontWeight: 700, fontSize: 15, color: "#111" }}>{req.item_name}</div>
                      <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>
                        Diminta: <strong>{req.requested_qty} {req.unit || ""}</strong>
                        {req.received_qty ? <> · Diterima: <strong style={{ color: "#16a34a" }}>{req.received_qty} {req.unit || ""}</strong></> : null}
                      </div>
                      {req.notes ? <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 3 }}>📝 {req.notes}</div> : null}
                      <div style={{ fontSize: 11, color: "#c4bfb5", marginTop: 4 }}>{tgl}</div>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
                      <span style={{
                        background: info.color + "18", color: info.color, border: `1px solid ${info.color}40`,
                        borderRadius: 5, fontSize: 12, fontWeight: 700, padding: "2px 10px",
                      }}>{info.label}</span>

                      {/* Klinik hanya bisa TERIMA saat barang sudah DIKIRIM gudang */}
                      {req.status === "DIKIRIM" && !isReceiving && (
                        <button
                          className="button button-primary"
                          style={{ fontSize: 13, padding: "5px 14px" }}
                          onClick={() => { setReceivingId(req.id); setReceiveQty(String(req.requested_qty)); }}
                        >
                          📦 Terima Barang
                        </button>
                      )}
                      {isReceiving && (
                        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                          <input
                            type="number" min="0" step="1"
                            value={receiveQty}
                            onChange={(e) => setReceiveQty(e.target.value)}
                            style={{ width: 80, padding: "5px 8px", border: "1px solid #e8e2d5", borderRadius: 6, fontSize: 14 }}
                            placeholder="Qty"
                          />
                          <span style={{ fontSize: 12, color: "#6b7280" }}>{req.unit || ""}</span>
                          <button className="button button-primary" style={{ fontSize: 13, padding: "5px 12px" }} onClick={() => confirmReceive(req)}>
                            ✓ Konfirmasi
                          </button>
                          <button className="button button-quiet" style={{ fontSize: 13, padding: "5px 10px" }} onClick={() => setReceivingId(null)}>
                            Batal
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </article>
      )}

      {mainTab === "master" && <>
      {/* ── Kartu Stok Habis / Limit ── */}
      {alertItems.length > 0 && showAlert && (
        <article className="panel" style={{ borderLeft: "4px solid #e53e3e" }}>
          <div className="panel-head" style={{ marginBottom: 10 }}>
            <div>
              <span className="eyebrow" style={{ color: "#c53030" }}>Perhatian</span>
              <h3 style={{ color: "#c53030" }}>Stok Habis / Di Bawah Minimum ({alertItems.length} item)</h3>
            </div>
            <button className="button button-quiet" style={{ fontSize: 12 }} onClick={() => setShowAlert(false)}>
              Sembunyikan
            </button>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {alertItems.map((r) => {
              const st = stockStatus(r);
              return (
                <div
                  key={r.id}
                  onClick={() => openStockAction(r)}
                  style={{
                    cursor: "pointer",
                    border: `1px solid ${st === "habis" ? "#fc8181" : "#f6ad55"}`,
                    background: st === "habis" ? "#fff5f5" : "#fffaf0",
                    borderRadius: 10,
                    padding: "10px 14px",
                    minWidth: 160,
                    maxWidth: 200,
                  }}
                >
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: st === "habis" ? "#c53030" : "#b7791f", marginBottom: 2 }}>
                    {st === "habis" ? "⛔ Habis" : "⚠️ Limit"}
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "#1a202c", marginBottom: 2 }}>{r.name}</div>
                  <div style={{ fontSize: 12, color: "#718096" }}>
                    Stok: <strong style={{ color: st === "habis" ? "#c53030" : "#b7791f" }}>{Number(r.current_stock)}</strong>
                    {" "}{r.stock_unit || r.unit || ""} / Min: {Number(r.minimum_stock)} {r.stock_unit || r.unit || ""}
                  </div>
                  <div style={{ fontSize: 11, color: "#a0aec0", marginTop: 2 }}>{usageLabel(r)}</div>
                </div>
              );
            })}
          </div>
        </article>
      )}

      {/* ── Error global — selalu tampil di atas ── */}
      {error && (
        <div className="alert alert-danger">
          <strong>Terjadi kesalahan.</strong> {error}
          {/does not exist|relation.*not found|table.*not found/i.test(error) && (
            <span style={{ display: 'block', marginTop: 6, fontSize: 13 }}>
              Tabel BHP mungkin belum dibuat. Pastikan migration SQL sudah dijalankan di Supabase.
            </span>
          )}
        </div>
      )}

      {/* ── Tombol Tambah Item BHP ── */}
      {!showForm && !editingId && (
        <div style={{ marginBottom: 12 }}>
          <button
            className="button button-primary"
            onClick={openCreateForm}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <Plus size={16} /> Tambah Item BHP
          </button>
        </div>
      )}

      {/* ── Form tambah/edit ── */}
      {(showForm || editingId) && (
      <article className="panel">
        <div className="panel-head">
          <div>
            <span className="eyebrow">Master Data XV</span>
            <h3>{editingId ? "Edit Item BHP" : "Tambah Item BHP"}</h3>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="button button-quiet" onClick={() => { setShowForm(false); resetForm(); }}>
              {editingId ? "Batal Edit" : "Tutup"}
            </button>
            <button className="button button-quiet" style={{ fontSize: 13 }} onClick={downloadTemplate}>
              ⬇ Template CSV
            </button>
            <button className="button button-soft" style={{ fontSize: 13 }} onClick={() => { setShowCsvImport(true); setCsvRows([]); setCsvError(''); setCsvResult(null); }}>
              📂 Import CSV
            </button>
          </div>
        </div>

        <form className="mt-form" onSubmit={handleSubmit}>
          <div className="mt-form-row">
            <div className="mt-field" style={{ flex: 2 }}>
              <label>Nama Item *</label>
              <input type="text" value={form.name} onChange={(e) => updateField("name", e.target.value)} required />
            </div>
            <div className="mt-field">
              <label>Kategori</label>
              <input type="text" value={form.category} onChange={(e) => updateField("category", e.target.value)} placeholder="Jarum, Kasa…" />
            </div>
          </div>
          <div className="mt-form-row">
            <div className="mt-field">
              <label>Satuan</label>
              <input type="text" value={form.unit} onChange={(e) => updateField("unit", e.target.value)} placeholder="pcs, box…" />
            </div>
            <div className="mt-field">
              <label>Stok Minimum</label>
              <input type="number" min="0" step="1" value={form.minimum_stock} onChange={(e) => updateField("minimum_stock", e.target.value)} />
            </div>
            <div className="mt-field">
              <label>Stok Target</label>
              <input type="number" min="0" step="1" value={form.target_stock} onChange={(e) => updateField("target_stock", e.target.value)} />
            </div>
            <div className="mt-field" style={{ flex: 2, minWidth: 280 }}>
              <label>Poli pemakai BHP *</label>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", paddingTop: 8 }}>
                {DIVISION_OPTIONS.filter((option) => option.value !== "ALL").map((option) => {
                  const checked = form.divisions.includes(option.value);
                  return (
                    <label key={option.value} style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13 }}>
                      <input type="checkbox" checked={checked} onChange={() => setForm((prev) => ({ ...prev, divisions: checked ? prev.divisions.filter((d) => d !== option.value) : [...prev.divisions, option.value] }))} />
                      {option.label}
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="mt-field">
              <label>Status</label>
              <select value={form.status} onChange={(e) => updateField("status", e.target.value)}>
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div className="mt-field mt-field-actions">
              <button type="submit" className="button button-primary" disabled={saving}>
                {saving ? "Menyimpan…" : editingId ? "Perbarui" : "Simpan"}
              </button>
            </div>
          </div>
        </form>
      </article>
      )}

      {/* ── Tabel Daftar BHP ── */}
      <article className="panel table-panel">
        <div style={{ padding: "24px 24px 0" }}>
        <div className="panel-head" style={{ paddingBottom: 10, paddingTop: 24 }}>
          <div>
            <span className="eyebrow">Inventory Klinik</span>
            <h3>Daftar Item BHP</h3>
          </div>
          <input
            className="search-input"
            type="search"
            placeholder="Cari nama, kode, kategori…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 220 }}
          />
        </div>

        {/* Tab divisi */}
        <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
          {DIV_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setDivTab(t.key)}
              style={{
                padding: "5px 16px",
                borderRadius: 20,
                border: "1px solid",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                borderColor: divTab === t.key ? "#2f6f4f" : "#d1d5db",
                background:  divTab === t.key ? "#2f6f4f" : "#fff",
                color:       divTab === t.key ? "#fff"    : "#374151",
              }}
            >
              {t.label}
              <span style={{
                marginLeft: 6,
                fontSize: 11,
                background: divTab === t.key ? "rgba(255,255,255,0.25)" : "#f3f4f6",
                color:      divTab === t.key ? "#fff" : "#6b7280",
                borderRadius: 10,
                padding: "1px 7px",
              }}>
                {t.key === "GUDANG"
                  ? rows.length
                  : rows.filter((r) => usedBy(r, t.key)).length
                }
              </span>
            </button>
          ))}
              <span style={{ marginLeft: "auto", fontSize: 12, color: "#9ca3af", alignSelf: "center" }}>
                {filtered.length} item ditampilkan
              </span>
          {selectedRows.length ? (
            <button className="button button-primary" style={{ padding: "5px 14px", fontSize: 13 }} onClick={() => openBatchRequest()}>
              {divTab === "GUDANG" ? "Buat Purchase Order" : `Minta ${selectedRows.length} Item`}
            </button>
          ) : null}
          {alertItems.length ? (
            <button className="button button-quiet" style={{ padding: "5px 14px", fontSize: 13 }} onClick={() => openBatchRequest(alertItems)}>
              {divTab === "GUDANG" ? "Buat Purchase Order" : "Minta Semua Limit"}
            </button>
          ) : null}
        </div>
        </div>{/* end padding wrapper */}

        {loading ? (
          <div className="content-loading">
            <div className="loader" />
            <span>Memuat data BHP…</span>
          </div>
          ) : (
          <>
          <div className="bhp-location-note">
            <strong>{locationLabel(divTab)}</strong>
            <span>Saldo, minimum, dan target di bawah adalah khusus lokasi stok ini.</span>
          </div>
          <table className="mt-table">
            <thead>
              <tr>
                <th style={{ width: 42 }}>
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && filtered.every((row) => selectedIds.has(row.id))}
                    onChange={toggleAllVisible}
                    aria-label="Pilih semua item tampil"
                  />
                </th>
                <th>Kode</th>
                <th>Item</th>
                <th>Divisi Penggunaan</th>
                <th>Stok</th>
                <th>Minimum</th>
                <th>Target</th>
                <th>Status</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => {
                const st = stockStatus(row);
                return (
                  <tr key={row.id} style={st !== "ok" ? { background: st === "habis" ? "#fff5f5" : "#fffbf0" } : {}}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(row.id)}
                        onChange={() => toggleSelected(row.id)}
                        aria-label={`Pilih ${row.name}`}
                      />
                    </td>
                    <td className="muted" style={{ fontSize: 12 }}>{row.item_code || "—"}</td>
                    <td>
                      <div className="primary-cell">
                        <strong>{row.name}</strong>
                        {row.category ? <span>{row.category}</span> : null}
                      </div>
                    </td>
                    <td>
                      <span className={`status status-${divTone(row.division)}`}>
                        {usageLabel(row)}
                      </span>
                    </td>
                    <td>
                      <strong style={{ color: st === "habis" ? "#c53030" : st === "limit" ? "#b7791f" : undefined }}>
                        {st === "habis" && <span title="Habis">⛔ </span>}
                        {st === "limit" && <span title="Di bawah minimum">⚠️ </span>}
                        {Number(row.current_stock).toLocaleString("id-ID")} {row.stock_unit || row.unit || ""}
                      </strong>
                    </td>
                    <td>{Number(row.minimum_stock).toLocaleString("id-ID")} {row.unit || ""}</td>
                    <td>{Number(row.target_stock).toLocaleString("id-ID")}  {row.unit || ""}</td>
                    <td>
                      <span className={`status status-${row.status === "ACTIVE" ? "positive" : "danger"}`}>
                        {row.status === "ACTIVE" ? "Aktif" : "Nonaktif"}
                      </span>
                    </td>
                    <td style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      {st !== "ok" && (
                        <button
                          className="link-button"
                          style={{ color: "#2563eb", fontWeight: 700 }}
                          onClick={() => openStockAction(row)}
                        >
                          {divTab === "GUDANG" ? "🛒 Purchase" : "📦 Minta Stok"}
                        </button>
                      )}
                      <button className="link-button" onClick={() => startEdit(row)}>Edit</button>
                      <button className="link-button link-danger" onClick={() => handleDelete(row)}>Hapus</button>
                    </td>
                  </tr>
                );
              })}
              {!filtered.length ? (
                <tr>
                  <td colSpan={9} className="muted">
                    {search ? "Tidak ada item yang cocok." : "Belum ada data BHP."}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
          </>
        )}
      </article>
      </> /* end mainTab === "master" */}

      {/* ── Tab Procurement ── */}
      {mainTab === "procurement" && (
        procLoading
          ? <div style={{ padding: 40, textAlign: "center", color: "#9ca3af" }}>Memuat data procurement…</div>
          : procData
            ? <ProcurementPage
                data={procData}
                identity={identity}
                initialItems={purchaseItems}
                onInitialItemsHandled={() => setPurchaseItems([])}
                onChanged={async () => {
                  setProcLoading(true);
                  try { setProcData(await loadProcurementData()); }
                  finally { setProcLoading(false); }
                  if (onChanged) onChanged();
                }}
              />
            : <div style={{ padding: 40, textAlign: "center", color: "#dc2626" }}>Gagal memuat data procurement.</div>
      )}

      {/* Modal permintaan stok */}
      {requestItem && (
        <BHPRequestModal
          item={requestItem}
          from_division={divTab !== "GUDANG" ? divTab : null}
          onClose={() => setRequestItem(null)}
          onSaved={() => {
            setRequestItem(null);
            setMainTab("requests");
            fetchRequests();
          }}
        />
      )}
      {requestItems.length ? (
        <BHPRequestModal
          items={requestItems}
          from_division={divTab !== "GUDANG" ? divTab : null}
          onClose={() => setRequestItems([])}
          onSaved={() => {
            setRequestItems([]);
            setSelectedIds(new Set());
            setMainTab("requests");
            fetchRequests();
          }}
        />
      ) : null}

      {/* ── Modal Import CSV ── */}
      {showCsvImport && (
        <div className="modal-backdrop" onClick={() => setShowCsvImport(false)}>
          <div className="modal-box csv-import-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <strong>Import Obat dari CSV</strong>
                <span style={{ display: 'block', fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                  Upload file CSV dari pemasok sesuai template yang sudah diunduh.
                </span>
              </div>
              <button className="btn-icon" onClick={() => setShowCsvImport(false)}>✕</button>
            </div>

            {/* Step 1: Download template */}
            <div className="csv-step">
              <div className="csv-step-num">1</div>
              <div className="csv-step-body">
                <div className="csv-step-title">Unduh Template</div>
                <p className="csv-step-desc">Kirimkan template ini ke pemasok untuk diisi sesuai daftar obat mereka.</p>
                <button className="button button-quiet" style={{ fontSize: 13 }} onClick={downloadTemplate}>
                  ⬇ Download template_obat_farmasi.csv
                </button>
              </div>
            </div>

            {/* Step 2: Upload CSV */}
            <div className="csv-step">
              <div className="csv-step-num">2</div>
              <div className="csv-step-body">
                <div className="csv-step-title">Upload File CSV</div>
                <p className="csv-step-desc">Pilih file CSV yang sudah diisi pemasok.</p>
                <label className="csv-upload-label">
                  <input
                    type="file" accept=".csv,text/csv"
                    style={{ display: 'none' }}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) parseCsvFile(f); e.target.value = ''; }}
                  />
                  📁 Pilih File CSV
                </label>
                {csvError && <div className="csv-error">{csvError}</div>}
              </div>
            </div>

            {/* Step 3: Preview */}
            {csvRows.length > 0 && !csvResult && (
              <div className="csv-step">
                <div className="csv-step-num">3</div>
                <div className="csv-step-body" style={{ width: '100%' }}>
                  <div className="csv-step-title">Preview — {csvRows.length} item ditemukan</div>
                  <div className="csv-preview-wrap">
                    <table className="csv-preview-table">
                      <thead>
                        <tr>
                          <th>#</th><th>Kode</th><th>Nama Obat</th><th>Kategori</th>
                          <th>Satuan</th><th>Min</th><th>Target</th><th>Divisi</th><th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {csvRows.map((r, i) => (
                          <tr key={i}>
                            <td style={{ color: '#9ca3af' }}>{r._row}</td>
                            <td>{r.item_code || '—'}</td>
                            <td><strong>{r.name}</strong></td>
                            <td>{r.category || '—'}</td>
                            <td>{r.unit || '—'}</td>
                            <td style={{ textAlign: 'right' }}>{r.minimum_stock}</td>
                            <td style={{ textAlign: 'right' }}>{r.target_stock}</td>
                            <td>
                              <span style={{ fontSize: 11, background: '#eff6ff', color: '#1d4ed8', padding: '2px 7px', borderRadius: 4 }}>
                                {r.division}
                              </span>
                            </td>
                            <td>
                              <span style={{ fontSize: 11, color: r.status === 'ACTIVE' ? '#16a34a' : '#6b7280' }}>
                                {r.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="modal-footer" style={{ marginTop: 16 }}>
                    <button className="button button-quiet" onClick={() => setCsvRows([])}>Ganti File</button>
                    <button
                      className="button button-primary"
                      disabled={csvImporting}
                      onClick={handleCsvImport}
                    >
                      {csvImporting ? `Mengimpor… (${csvRows.length} item)` : `✓ Import ${csvRows.length} Item`}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Hasil */}
            {csvResult && (
              <div className="csv-result">
                <div style={{ fontSize: 28 }}>{csvResult.fail === 0 ? '✅' : '⚠️'}</div>
                <div>
                  <strong>{csvResult.ok} item berhasil diimpor</strong>
                  {csvResult.fail > 0 && <span style={{ color: '#dc2626' }}> · {csvResult.fail} gagal (mungkin duplikat)</span>}
                </div>
                <button className="button button-primary" onClick={() => setShowCsvImport(false)}>Selesai</button>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        .csv-import-modal { width: 780px; max-width: 96vw; max-height: 90vh; overflow-y: auto; }
        .csv-step { display: flex; gap: 14px; padding: 14px 0; border-bottom: 1px solid #f3f4f6; }
        .csv-step:last-of-type { border-bottom: none; }
        .csv-step-num {
          width: 28px; height: 28px; border-radius: 50%;
          background: #0369a1; color: #fff; font-size: 13px; font-weight: 700;
          display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 2px;
        }
        .csv-step-body { flex: 1; }
        .csv-step-title { font-size: 14px; font-weight: 600; color: #1e293b; margin-bottom: 4px; }
        .csv-step-desc  { font-size: 13px; color: #6b7280; margin: 0 0 10px; }
        .csv-upload-label {
          display: inline-block; padding: 8px 18px;
          background: #f0f9ff; border: 1.5px dashed #0369a1; border-radius: 8px;
          color: #0369a1; font-size: 13px; font-weight: 600; cursor: pointer;
        }
        .csv-upload-label:hover { background: #e0f2fe; }
        .csv-error { color: #dc2626; font-size: 13px; margin-top: 8px; }
        .csv-preview-wrap { overflow-x: auto; border: 1px solid #e5e7eb; border-radius: 8px; }
        .csv-preview-table { width: 100%; border-collapse: collapse; font-size: 12px; }
        .csv-preview-table th {
          background: #f8fafc; padding: 7px 10px; text-align: left;
          font-weight: 600; color: #6b7280; border-bottom: 1px solid #e5e7eb;
        }
        .csv-preview-table td { padding: 6px 10px; border-bottom: 1px solid #f3f4f6; }
        .csv-preview-table tr:last-child td { border-bottom: none; }
        .csv-result {
          display: flex; flex-direction: column; align-items: center; gap: 12px;
          padding: 24px; text-align: center;
        }
        .mt-form-row { display: flex; gap: 16px; flex-wrap: wrap; margin-bottom: 14px; }
        .mt-field { flex: 1; min-width: 150px; display: flex; flex-direction: column; gap: 6px; }
        .mt-field label { font-size: 12px; font-weight: 600; color: #6b6555; }
        .mt-field input, .mt-field select {
          padding: 9px 12px; border: 1px solid #e8e2d5; border-radius: 8px; font-size: 14px; background: #fff;
        }
        .mt-field-actions { flex-direction: row; align-items: flex-end; gap: 10px; min-width: unset; }
        .mt-table { width: 100%; border-collapse: collapse; font-size: 14px; }
        .mt-table th {
          text-align: left; font-size: 12px; text-transform: uppercase;
          letter-spacing: 0.03em; color: #7a7364; padding: 10px 16px; border-bottom: 1px solid #e8e2d5;
        }
        .mt-table th:first-child { padding-left: 24px; }
        .mt-table th:last-child  { padding-right: 24px; }
        .mt-table td { padding: 12px 16px; border-bottom: 1px solid #f1ece0; vertical-align: middle; }
        .mt-table td:first-child { padding-left: 24px; }
        .mt-table td:last-child  { padding-right: 24px; }
        .link-button { background: none; border: none; padding: 0; font-size: 13px; font-weight: 600; color: #2f6f4f; cursor: pointer; }
        .link-danger { color: #c0392b; }
        .search-input { padding: 8px 12px; border: 1px solid #e8e2d5; border-radius: 8px; font-size: 14px; }
        .bhp-location-note { align-items:baseline; background:#f4fbf5; border:1px solid #d9eedb; border-radius:8px; color:#6b8172; display:flex; gap:8px; margin:0 24px 10px; padding:9px 12px; }
        .bhp-location-note strong { color:#24533d; font-size:13px; }
        .bhp-location-note span { font-size:12px; }
        @media (max-width: 760px) { .bhp-location-note { align-items:flex-start; flex-direction:column; gap:3px; } }
      `}</style>
    </div>
  );
}
